import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Search,
  ExternalLink,
  Play,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Wrench,
} from 'lucide-react';
import { AdminSelect } from './AdminSelect';
import { CandidateUnifiedModal } from './CandidateUnifiedModal';
import {
  listLinkHealthChecks,
  getLinkHealthSummary,
  getLinkHealthStatus,
  triggerLinkHealthScan,
  bulkSetLinkVisibility,
  scanSelectedLinks,
  getLastRunChanges,
  getDomainFieldCounts,
  getCoverageTalentIds,
  listPipelineCandidates,
  type LinkHealthCheck,
  type LinkHealthSummary,
  type LinkHealthStatus,
  type DomainFieldCountsResponse,
  type ScanChange,
  type PipelineCandidate,
} from '../../services/api';

type HealthTab = 'links' | 'coverage';

const PAGE_SIZE = 50;

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  ok: { label: 'Valid', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  broken: { label: 'Broken', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  replaced: { label: 'Replaced', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  skipped: { label: 'Skipped', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

const LINK_TYPE_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  scholar: 'Scholar',
  teaching: 'Teaching',
  additional: 'Additional',
};

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SortHeader({ label, sortKey, sort, onSort }: {
  label: string; sortKey: string;
  sort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
}) {
  const isActive = sort?.col === sortKey;
  return (
    <th className="px-3 py-3 text-left font-medium text-gray-500 dark:text-gray-400">
      <button onClick={() => onSort(sortKey)} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
        {label}
        <span className="flex flex-col">
          <ChevronUp className={`w-2.5 h-2.5 -mb-0.5 ${isActive && sort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
          <ChevronDown className={`w-2.5 h-2.5 ${isActive && sort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
        </span>
      </button>
    </th>
  );
}

function countColor(count: number): string {
  if (count === 0) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (count < 3) return 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
  return 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300';
}

function CovSortIcon({ col, sort }: { col: string; sort: { col: string; dir: 'asc' | 'desc' } | null }) {
  const isActive = sort?.col === col;
  return (
    <span className="flex flex-col">
      <ChevronUp className={`w-2.5 h-2.5 -mb-0.5 ${isActive && sort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
      <ChevronDown className={`w-2.5 h-2.5 ${isActive && sort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
    </span>
  );
}

// ─── Candidate Coverage Tab (per tenant) ─────────────────────────────────────

function CoverageTab() {
  const [data, setData] = useState<DomainFieldCountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedTenant, setExpandedTenant] = useState<number | null>(null);
  const [talentModal, setTalentModal] = useState<{
    tenantName: string; domain: string; field: string;
    talents: { talentId: string; name: string | null; field: string | null; status: string; sentAt: string | null }[];
  } | null>(null);
  const [covSearch, setCovSearch] = useState('');
  const [covSort, setCovSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);

  useEffect(() => {
    getDomainFieldCounts().then(setData).finally(() => setLoading(false));
  }, []);

  const handleFieldClick = async (tenantName: string, domainKey: string, field: string) => {
    const res = await getCoverageTalentIds(tenantName, domainKey, field);
    setCovSearch(''); setCovSort(null);
    setTalentModal({ tenantName, domain: domainKey, field, talents: res.talents });
  };

  const handleCovSort = (col: string) => {
    setCovSort(prev => {
      if (prev?.col === col) return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      return { col, dir: 'asc' };
    });
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>;
  if (!data) return null;

  const labels = data.domainLabels;

  // Collect all issues across tenants
  const issues: { tenant: string; domainKey: string; field: string; count: number }[] = [];
  for (const t of data.tenants) {
    for (const s of t.sections) {
      if (s.count < 3) issues.push({ tenant: t.name, domainKey: s.domainKey, field: s.field, count: s.count });
    }
  }
  const zeros = issues.filter(i => i.count === 0);
  const lows = issues.filter(i => i.count > 0);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Talent Coverage by Tenant</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Talent counts per tenant, showing only domains and fields enabled in their account configuration. Counts reflect candidates in packets sent to each tenant.</p>
      </div>

      {/* Alert summary */}
      {issues.length > 0 && (
        <div className="mb-6 p-4 rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10 space-y-1">
          {zeros.length > 0 && (
            <div className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-400">
                <strong>{zeros.length}</strong> empty field{zeros.length !== 1 ? 's' : ''} across tenants
              </span>
            </div>
          )}
          {lows.length > 0 && (
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span className="text-sm text-amber-700 dark:text-amber-400">
                <strong>{lows.length}</strong> low-coverage field{lows.length !== 1 ? 's' : ''} (&lt;3)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tenant cards */}
      <div className="space-y-3">
        {data.tenants.map(tenant => {
          const isExpanded = expandedTenant === tenant.id;
          const tenantIssues = tenant.sections.filter(s => s.count < 3).length;
          const totalCandidates = tenant.sections.reduce((s, f) => s + f.count, 0);

          // Group sections by domain
          const byDomain = new Map<string, { field: string; count: number }[]>();
          for (const s of tenant.sections) {
            if (!byDomain.has(s.domainKey)) byDomain.set(s.domainKey, []);
            byDomain.get(s.domainKey)!.push({ field: s.field, count: s.count });
          }

          return (
            <div key={tenant.id} className={`rounded-xl border overflow-hidden ${
              tenantIssues > 0 ? 'border-red-200 dark:border-red-800/50' : 'border-gray-200 dark:border-gray-800'
            } bg-white dark:bg-gray-900`}>
              <button
                onClick={() => setExpandedTenant(isExpanded ? null : tenant.id)}
                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  <div className="text-left">
                    <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{tenant.name || tenant.domain}</span>
                    <span className="text-xs text-gray-400 ml-2">{tenant.domain}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {tenantIssues > 0 && (
                    <span className="text-xs font-medium text-red-600 dark:text-red-400">{tenantIssues} gap{tenantIssues !== 1 ? 's' : ''}</span>
                  )}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${countColor(totalCandidates)}`}>{totalCandidates} total</span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-0 divide-x divide-gray-100 dark:divide-gray-800">
                    {Array.from(byDomain.entries()).map(([domainKey, fields]) => {
                      const domainTotal = fields.reduce((s, f) => s + f.count, 0);
                      return (
                        <div key={domainKey} className="min-w-0">
                          <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{labels[domainKey] || domainKey}</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${countColor(domainTotal)}`}>{domainTotal}</span>
                          </div>
                          <div className="divide-y divide-gray-50 dark:divide-gray-800">
                            {fields.map((f, i) => (
                              <div key={i} className="relative group">
                                <button
                                  onClick={() => f.count > 0 ? handleFieldClick(tenant.name, domainKey, f.field) : undefined}
                                  className={`w-full flex items-center justify-between px-4 py-2 text-left transition-colors ${
                                    f.count === 0
                                      ? 'bg-red-50/50 dark:bg-red-900/10 cursor-default'
                                      : f.count < 3
                                        ? 'bg-amber-50/30 dark:bg-amber-900/5 hover:bg-amber-50 dark:hover:bg-amber-900/10 cursor-pointer'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer'
                                  }`}
                                >
                                  <span className={`text-sm ${f.count === 0 ? 'text-red-700 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
                                    {f.field}
                                  </span>
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full min-w-[1.75rem] text-center ${countColor(f.count)}`}>
                                    {f.count}
                                  </span>
                                </button>
                                {f.count === 0 && (
                                  <span className="invisible group-hover:visible absolute right-2 bottom-full mb-1 w-56 p-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-normal shadow-lg z-10">
                                    This field/domain is in the account config but no talent has been published yet.
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.tenants.length === 0 && (
        <div className="text-center py-12 text-gray-400">No tenants with enabled domains found.</div>
      )}

      {/* Talent list modal */}
      {talentModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setTalentModal(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-3xl mx-4 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {talentModal.talents.length} Candidate{talentModal.talents.length !== 1 ? 's' : ''}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {talentModal.tenantName} &middot; {labels[talentModal.domain] || talentModal.domain} &middot; {talentModal.field}
                </p>
              </div>
              <button onClick={() => setTalentModal(null)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            {/* Search */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="text" placeholder="Search name or ID..." value={covSearch} onChange={e => setCovSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 overscroll-contain">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-5 py-2.5"><button onClick={() => handleCovSort('name')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">Name <CovSortIcon col="name" sort={covSort} /></button></th>
                    <th className="px-5 py-2.5">Talent ID</th>
                    <th className="px-5 py-2.5">Field</th>
                    <th className="px-5 py-2.5"><button onClick={() => handleCovSort('sent')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">Sent to Customer <CovSortIcon col="sent" sort={covSort} /></button></th>
                    <th className="px-5 py-2.5"><button onClick={() => handleCovSort('sentAt')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">Sent Date <CovSortIcon col="sentAt" sort={covSort} /></button></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {(() => {
                    let filtered = talentModal.talents;
                    if (covSearch) {
                      const q = covSearch.toLowerCase();
                      filtered = filtered.filter(t => (t.name || '').toLowerCase().includes(q) || t.talentId.toLowerCase().includes(q));
                    }
                    if (covSort) {
                      const dir = covSort.dir === 'asc' ? 1 : -1;
                      filtered = [...filtered].sort((a, b) => {
                        let va: any, vb: any;
                        switch (covSort.col) {
                          case 'name': va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
                          case 'sent': va = a.status === 'accepted' ? 0 : 1; vb = b.status === 'accepted' ? 0 : 1; break;
                          case 'sentAt': va = a.sentAt || ''; vb = b.sentAt || ''; break;
                          default: return 0;
                        }
                        return va < vb ? -dir : va > vb ? dir : 0;
                      });
                    }
                    if (filtered.length === 0) return <tr><td colSpan={5} className="p-8 text-center text-gray-400">No talents found{covSearch ? ' matching search' : ''}.</td></tr>;
                    return filtered.map(t => {
                      const isSent = t.status === 'accepted';
                      return (
                        <tr key={t.talentId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-5 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{t.name || '—'}</td>
                          <td className="px-5 py-2.5 font-mono text-gray-600 dark:text-gray-400 text-xs">{t.talentId}</td>
                          <td className="px-5 py-2.5 text-gray-600 dark:text-gray-300">{t.field || '—'}</td>
                          <td className="px-5 py-2.5">
                            {isSent ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                <CheckCircle2 className="w-3 h-3" /> Yes
                              </span>
                            ) : (
                              <span className="group relative inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 cursor-help">
                                <AlertTriangle className="w-3 h-3" /> No
                                <span className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-normal shadow-lg z-10">
                                  Once you approve this talent, it will be auto-published. Talent was published before but moved to in review later.
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                            {t.sentAt ? formatDate(t.sentAt) : '—'}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Link Health Tab ─────────────────────────────────────────────────────────

function LinksTab() {
  const [checks, setChecks] = useState<LinkHealthCheck[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<LinkHealthSummary | null>(null);
  const [scanStatus, setScanStatus] = useState<LinkHealthStatus | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [linkTypeFilter, setLinkTypeFilter] = useState('');
  const [hiddenFilter, setHiddenFilter] = useState('');
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const selectedMap = useRef<Map<number, LinkHealthCheck>>(new Map());
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showChanges, setShowChanges] = useState<ScanChange[] | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<PipelineCandidate | null>(null);
  const [editedLinks, setEditedLinks] = useState<Set<number>>(new Set());
  const [rescanningId, setRescanningId] = useState<number | null>(null);

  const fetchData = useCallback(async (p = page, s = debouncedSearch) => {
    setLoading(true);
    try {
      const [checksRes, summaryRes, statusRes] = await Promise.all([
        listLinkHealthChecks({
          page: p, pageSize: PAGE_SIZE, search: s,
          status: statusFilter, linkType: linkTypeFilter, hidden: hiddenFilter,
          sortCol: sort?.col, sortDir: sort?.dir,
        }),
        getLinkHealthSummary(),
        getLinkHealthStatus(),
      ]);
      setChecks(checksRes.data);
      setTotal(checksRes.total);
      setSummary(summaryRes);
      setScanStatus(statusRes);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, linkTypeFilter, hiddenFilter, sort]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search]);

  useEffect(() => {
    if (scanStatus?.isRunning) {
      pollRef.current = setInterval(async () => {
        const status = await getLinkHealthStatus();
        setScanStatus(status);
        if (!status.isRunning) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          fetchData();
        }
      }, 5000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [scanStatus?.isRunning, fetchData]);

  const handleSort = (col: string) => {
    setSort(prev => {
      if (prev?.col === col) return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      return { col, dir: 'asc' };
    });
    setPage(1);
  };

  const handleRunNow = async () => {
    setTriggerLoading(true);
    try {
      await triggerLinkHealthScan();
      const status = await getLinkHealthStatus();
      setScanStatus(status);
    } finally { setTriggerLoading(false); }
  };

  const handleBulkVisibility = async (hidden: boolean) => {
    setBulkLoading(true);
    try {
      await bulkSetLinkVisibility(Array.from(selected), hidden);
      setSelected(new Set());
      selectedMap.current.clear();
      await fetchData();
    } finally { setBulkLoading(false); }
  };

  const toggleSelect = (check: LinkHealthCheck) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(check.id)) { n.delete(check.id); selectedMap.current.delete(check.id); }
      else { n.add(check.id); selectedMap.current.set(check.id, check); }
      return n;
    });
  };
  const toggleSelectAll = () => {
    const allPageSelected = checks.length > 0 && checks.every(c => selected.has(c.id));
    setSelected(prev => {
      const n = new Set(prev);
      for (const c of checks) {
        if (allPageSelected) { n.delete(c.id); selectedMap.current.delete(c.id); }
        else { n.add(c.id); selectedMap.current.set(c.id, c); }
      }
      return n;
    });
  };
  const clearFilters = () => { setSearch(''); setDebouncedSearch(''); setStatusFilter(''); setLinkTypeFilter(''); setHiddenFilter(''); setSort(null); setPage(1); };

  const hasFilters = search || statusFilter || linkTypeFilter || hiddenFilter;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const [openTab, setOpenTab] = useState<'view' | 'edit'>('view');
  const [scrollToField, setScrollToField] = useState<string | undefined>();
  const [editorTab, setEditorTab] = useState<string | undefined>();
  const [loadingCheckId, setLoadingCheckId] = useState<number | null>(null);

  const LINK_TYPE_TO_EDITOR_TAB: Record<string, string> = { additional: 'vetting' };

  const openProfile = async (check: LinkHealthCheck, tab: 'view' | 'edit' = 'view') => {
    setOpenTab(tab);
    setScrollToField(tab === 'edit' ? check.linkType : undefined);
    setEditorTab(tab === 'edit' ? (LINK_TYPE_TO_EDITOR_TAB[check.linkType] || 'info') : undefined);
    setLoadingCheckId(check.id);
    try {
      const res = await listPipelineCandidates({ search: check.talentId, pageSize: 1 });
      const m = res.candidates.find(c => c.talentId === check.talentId);
      if (m) {
        setSelectedProfile(m);
      } else {
        const stub: PipelineCandidate = {
          id: 0, talentId: check.talentId, name: check.talentName || check.talentId,
          domain: 'swe', field: null, status: '', linkedinVerified: '', resumeVerification: '',
          webAgentVerification: '', agentRecommendation: '', hourlyRate: null, uploadGroup: '',
          addedBy: '', addedAt: '', reviewedBy: null, reviewedAt: null, batches: [], enrichment: {}, runId: '', reviewNotes: null, reviewTags: null, isGolden: false,
        };
        setSelectedProfile(stub);
      }
    } catch {
      /* silent */
    } finally {
      setLoadingCheckId(null);
    }
  };

  const handleRescan = async (check: LinkHealthCheck) => {
    setRescanningId(check.id);
    try {
      await scanSelectedLinks([check.id]);
      setEditedLinks(prev => { const n = new Set(prev); n.delete(check.id); return n; });
      const status = await getLinkHealthStatus();
      setScanStatus(status);
    } finally {
      setRescanningId(null);
    }
  };

  return (
    <>
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {scanStatus?.lastRunAt && !scanStatus.isRunning && (
            <span className="text-xs text-gray-400">
              Last run: {formatDate(scanStatus.lastRunAt)}
              {scanStatus.lastRunStats && ` (${formatDuration(scanStatus.lastRunStats.durationMs)})`}
            </span>
          )}
        </div>
        <button onClick={handleRunNow} disabled={scanStatus?.isRunning || triggerLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {scanStatus?.isRunning ? <><Loader2 className="w-4 h-4 animate-spin" />Scanning...</> : <><Play className="w-4 h-4" />Scan All</>}
        </button>
      </div>

      {/* Scan progress */}
      {scanStatus?.isRunning && scanStatus.progress && (
        <div className="mb-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50/50 dark:bg-blue-900/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">{scanStatus.progress.phase}</span>
            </div>
            <span className="text-xs text-blue-600 dark:text-blue-400">
              {scanStatus.progress.completed} / {scanStatus.progress.totalLinks} links
              {scanStatus.progress.broken > 0 && ` · ${scanStatus.progress.broken} broken`}
            </span>
          </div>
          {scanStatus.progress.totalLinks > 0 && (
            <div className="w-full h-2 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, (scanStatus.progress.completed / scanStatus.progress.totalLinks) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Scan just completed */}
      {!scanStatus?.isRunning && scanStatus?.lastRunStats && scanStatus.lastRunAt && (Date.now() - new Date(scanStatus.lastRunAt).getTime() < 60000) && (
        <button
          onClick={async () => { const c = await getLastRunChanges(); setShowChanges(c); }}
          className="mb-4 p-3 rounded-xl border border-green-200 dark:border-green-800/50 bg-green-50/50 dark:bg-green-900/10 flex items-center justify-between w-full text-left hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-800 dark:text-green-300">Scan complete</span>
            <span className="text-xs text-green-600 dark:text-green-400">— click to view changes</span>
          </div>
          <span className="text-xs text-green-600 dark:text-green-400">
            {scanStatus.lastRunStats.checked} checked · {scanStatus.lastRunStats.broken} broken · {scanStatus.lastRunStats.replaced} replaced · {scanStatus.lastRunStats.hidden} hidden · {scanStatus.lastRunStats.unhidden} unhidden · {formatDuration(scanStatus.lastRunStats.durationMs)}
          </span>
        </button>
      )}

      {scanStatus?.lastError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />Last scan error: {scanStatus.lastError}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Links', value: summary.total, icon: RefreshCw, color: 'text-blue-600 dark:text-blue-400', filter: () => { setStatusFilter(''); setHiddenFilter(''); setPage(1); } },
            { label: 'Valid', value: summary.ok, icon: CheckCircle2, color: 'text-green-600 dark:text-green-400', filter: () => { setStatusFilter('ok'); setHiddenFilter(''); setPage(1); } },
            { label: 'Broken', value: summary.broken, icon: XCircle, color: 'text-red-600 dark:text-red-400', filter: () => { setStatusFilter('broken'); setHiddenFilter(''); setPage(1); } },
            { label: 'Hidden', value: summary.hidden, icon: EyeOff, color: 'text-amber-600 dark:text-amber-400', filter: () => { setStatusFilter(''); setHiddenFilter('true'); setPage(1); } },
          ].map(card => (
            <button key={card.label} onClick={card.filter}
              className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-left hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <card.icon className={`w-4 h-4 ${card.color}`} />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</span>
              </div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{card.value.toLocaleString()}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search name, talent ID, or URL..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
        </div>
        <AdminSelect value={statusFilter} onChange={v => { setStatusFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Status' }, { value: 'ok', label: 'Valid' }, { value: 'broken', label: 'Broken' }, { value: 'replaced', label: 'Replaced' }]}
          placeholder="All Status" className="w-36" />
        <AdminSelect value={linkTypeFilter} onChange={v => { setLinkTypeFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Types' }, { value: 'linkedin', label: 'LinkedIn' }, { value: 'scholar', label: 'Scholar' }, { value: 'teaching', label: 'Teaching' }, { value: 'additional', label: 'Additional' }]}
          placeholder="All Types" className="w-36" />
        <AdminSelect value={hiddenFilter} onChange={v => { setHiddenFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Visibility' }, { value: 'true', label: 'Hidden' }, { value: 'false', label: 'Visible' }]}
          placeholder="All Visibility" className="w-36" />
      </div>

      {/* Active filter/sort pills */}
      {(debouncedSearch || sort || statusFilter || linkTypeFilter || hiddenFilter) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs font-medium text-gray-500">Active filters:</span>
          {debouncedSearch && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-800">
              <span className="font-medium">Search:</span>
              <span>{debouncedSearch}</span>
              <button onClick={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }} className="p-0.5 hover:bg-purple-100 dark:hover:bg-purple-800 rounded text-purple-500 hover:text-purple-800 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {sort && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800">
              <span className="font-medium">Sort:</span>
              <span>{{ talentName: 'Talent', linkType: 'Type', url: 'URL', status: 'Status', reason: 'Reason', hidden: 'Visibility', lastCheckedAt: 'Checked' }[sort.col] || sort.col} ({sort.dir})</span>
              <button onClick={() => { setSort(null); setPage(1); }} className="p-0.5 hover:bg-green-100 dark:hover:bg-green-800 rounded text-green-500 hover:text-green-800 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {statusFilter && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
              <span className="font-medium">Status:</span>
              <span>{{ ok: 'Valid', broken: 'Broken', replaced: 'Replaced' }[statusFilter] || statusFilter}</span>
              <button onClick={() => { setStatusFilter(''); setPage(1); }} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded text-blue-500 hover:text-blue-800 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {linkTypeFilter && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
              <span className="font-medium">Type:</span>
              <span>{LINK_TYPE_LABELS[linkTypeFilter] || linkTypeFilter}</span>
              <button onClick={() => { setLinkTypeFilter(''); setPage(1); }} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded text-blue-500 hover:text-blue-800 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {hiddenFilter && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
              <span className="font-medium">Visibility:</span>
              <span>{hiddenFilter === 'true' ? 'Hidden' : 'Visible'}</span>
              <button onClick={() => { setHiddenFilter(''); setPage(1); }} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded text-blue-500 hover:text-blue-800 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700 underline ml-2">Clear all</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
              <th className="w-10 px-3 py-3">
                <input type="checkbox" checked={checks.length > 0 && checks.every(c => selected.has(c.id))} onChange={toggleSelectAll} className="rounded border-gray-300" />
              </th>
              <SortHeader label="Talent" sortKey="talentName" sort={sort} onSort={handleSort} />
              <SortHeader label="Type" sortKey="linkType" sort={sort} onSort={handleSort} />
              <SortHeader label="URL" sortKey="url" sort={sort} onSort={handleSort} />
              <SortHeader label="Status" sortKey="status" sort={sort} onSort={handleSort} />
              <SortHeader label="Reason" sortKey="reason" sort={sort} onSort={handleSort} />
              <SortHeader label="Visibility" sortKey="hidden" sort={sort} onSort={handleSort} />
              <SortHeader label="Checked" sortKey="lastCheckedAt" sort={sort} onSort={handleSort} />
              <th className="px-3 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && checks.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
            ) : checks.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">No URL checks found{hasFilters ? ' matching filters' : ''}.</td></tr>
            ) : checks.map(check => {
              const badge = STATUS_BADGES[check.status] || STATUS_BADGES.skipped;
              return (
                <tr key={check.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selected.has(check.id)} onChange={() => toggleSelect(check)} className="rounded border-gray-300" />
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => openProfile(check)} className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-left">
                      {check.talentName || check.talentId}
                    </button>
                    {check.talentName && <div className="text-xs text-gray-400 mt-0.5">{check.talentId}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">
                    {LINK_TYPE_LABELS[check.linkType] || check.linkType}
                    {check.linkIndex >= 0 && <span className="text-gray-400 ml-1">[{check.linkIndex}]</span>}
                  </td>
                  <td className="px-3 py-2.5 max-w-[300px]">
                    <a href={check.url} target="_blank" rel="noopener noreferrer" title={check.url}
                      className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 group">
                      <span className="truncate">{check.url}</span>
                      <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>{badge.label}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-[150px] truncate" title={check.reason || ''}>{check.reason || '—'}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={async () => { await bulkSetLinkVisibility([check.id], !check.hidden); fetchData(); }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${check.hidden ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20' : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
                      title={check.hidden ? 'Click to show' : 'Click to hide'}>
                      {check.hidden ? <><EyeOff className="w-3 h-3" /> Hidden</> : <><Eye className="w-3 h-3" /> Visible</>}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-400 whitespace-nowrap">{check.lastCheckedAt ? formatDate(check.lastCheckedAt) : '—'}</td>
                  <td className="px-3 py-2.5">
                    {editedLinks.has(check.id) ? (
                      <button
                        onClick={() => handleRescan(check)}
                        disabled={rescanningId === check.id || scanStatus?.isRunning}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                      >
                        {rescanningId === check.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rescan
                      </button>
                    ) : check.status === 'broken' ? (
                      <button
                        onClick={() => openProfile(check, 'edit')}
                        disabled={loadingCheckId === check.id}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                      >
                        {loadingCheckId === check.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />} Fix
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">Showing {from}–{to} of {total.toLocaleString()}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-gray-600 dark:text-gray-300">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Floating bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-xl">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="w-px h-5 bg-gray-700 dark:bg-gray-300" />
          <button onClick={() => handleBulkVisibility(true)} disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            <EyeOff className="w-3.5 h-3.5" /> Hide
          </button>
          <button onClick={() => handleBulkVisibility(false)} disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
            <Eye className="w-3.5 h-3.5" /> Show
          </button>
          <div className="w-px h-5 bg-gray-700 dark:bg-gray-300" />
          <button onClick={async () => {
              setBulkLoading(true);
              try { await scanSelectedLinks(Array.from(selected)); setSelected(new Set()); selectedMap.current.clear(); const s = await getLinkHealthStatus(); setScanStatus(s); } finally { setBulkLoading(false); }
            }} disabled={bulkLoading || scanStatus?.isRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            <Play className="w-3.5 h-3.5" /> Re-scan
          </button>
          <button onClick={() => { setSelected(new Set()); selectedMap.current.clear(); }} className="p-1.5 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {selectedProfile && (
        <CandidateUnifiedModal profile={selectedProfile} allProfiles={[selectedProfile]} initialTab={openTab}
          onClose={() => { setSelectedProfile(null); setScrollToField(undefined); setEditorTab(undefined); }} onApprove={() => {}} onRemove={() => {}} onNavigate={() => {}}
          scrollToField={scrollToField}
          initialEditorTab={editorTab}
          onEditorSaved={() => {
            const editedCheck = checks.find(c => c.talentId === selectedProfile.talentId);
            if (editedCheck) {
              setEditedLinks(prev => { const n = new Set(prev); n.add(editedCheck.id); return n; });
            }
            fetchData();
          }} />
      )}

      {/* Scan changes modal */}
      {showChanges && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowChanges(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-3xl mx-4 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Scan Results</h3>
                <p className="text-xs text-gray-500 mt-0.5">{showChanges.length} link{showChanges.length !== 1 ? 's' : ''} changed</p>
              </div>
              <button onClick={() => setShowChanges(null)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            {showChanges.length > 0 ? (
              <div className="overflow-y-auto flex-1 overscroll-contain">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm">
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-2.5">Talent</th>
                      <th className="px-5 py-2.5">Type</th>
                      <th className="px-5 py-2.5">URL</th>
                      <th className="px-5 py-2.5">Action</th>
                      <th className="px-5 py-2.5">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {showChanges.map((c, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-5 py-2.5">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{c.talentName}</div>
                          <div className="text-xs text-gray-400 font-mono">{c.talentId}</div>
                        </td>
                        <td className="px-5 py-2.5 text-gray-600 dark:text-gray-300">{LINK_TYPE_LABELS[c.linkType] || c.linkType}</td>
                        <td className="px-5 py-2.5 max-w-[200px]">
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-600 text-xs truncate block" title={c.url}>{c.url}</a>
                        </td>
                        <td className="px-5 py-2.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            c.action === 'hidden' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            c.action === 'unhidden' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            c.action === 'replaced' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            c.action === 'verified' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>{c.action}</span>
                        </td>
                        <td className="px-5 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-[150px] truncate" title={c.reason}>{c.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">No changes in last scan — all links unchanged.</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ─── Main Section with Tabs ──────────────────────────────────────────────────

const TABS: { key: HealthTab; label: string }[] = [
  { key: 'links', label: 'Profile Links' },
  { key: 'coverage', label: 'Talent Coverage' },
];

export const LinkHealthSection: React.FC = () => {
  const [tab, setTab] = useState<HealthTab>('links');

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Health Check</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Monitor link validity and candidate coverage across domains.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-800">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t.key
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'links' && <LinksTab />}
      {tab === 'coverage' && <CoverageTab />}
    </div>
  );
};
